import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Award,
  BookOpen,
  Check,
  CheckCheck,
  CheckCircle,
  Clock,
  Globe,
  HelpCircle,
  Info,
  Lock,
  MapPin,
  MoreVertical,
  Send,
  Shield,
  Smile,
  Trash2,
  User,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';

import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { ticketService } from '@/services/ticket.service';
import { useAuth } from '@/hooks/useAuth';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import { LEARNER_TICKETS_PERMISSION } from '@/constants/constant-permissions';
import {
  SelfTicketItemType,
  SelfTicketRequestStatus,
  SelfTicketRequestResponseDto,
  TicketMessageDto,
  MessageReactionDto,
} from '@/types/ticket.types';

// Chat message interface for UI display
interface ChatMessage {
  id: string;
  sender_id: string;
  sender_name: string;
  sender_role: 'requester' | 'admin' | 'manager' | 'system';
  message: string;
  timestamp: string;
  is_read_by: string[];
  reactions?: MessageReactionDto[];
  is_deleted?: boolean;
}

// Map API message to UI ChatMessage format
const mapApiMessageToChat = (msg: TicketMessageDto, _current_user_id: string): ChatMessage => ({
  id: msg._id,
  sender_id: msg.sender_id,
  sender_name: msg.sender_name,
  sender_role: msg.sender_role === 'REQUESTER' ? 'requester' : msg.sender_role === 'SYSTEM' ? 'system' : 'admin',
  message: msg.message,
  timestamp: msg.sent_at,
  is_read_by: msg.is_read_by || [],
  reactions: msg.reactions,
  is_deleted: msg.is_deleted,
});

// Common reaction emojis
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '👎'];

// Form interface
interface MessageFormData {
  message: string;
}

// Type configurations
const typeConfigMap: Record<SelfTicketItemType, { icon: typeof BookOpen; label: string; color: string; bgColor: string }> = {
  [SelfTicketItemType.COURSE]: {
    icon: BookOpen,
    label: 'Course',
    color: 'text-brand-indigo-600',
    bgColor: 'bg-brand-indigo-100',
  },
  [SelfTicketItemType.CERTIFICATION]: {
    icon: Award,
    label: 'Certification',
    color: 'text-brand-amber-600',
    bgColor: 'bg-brand-amber-100',
  },
  [SelfTicketItemType.LEARNING_PATH]: {
    icon: MapPin,
    label: 'Learning Path',
    color: 'text-brand-green-600',
    bgColor: 'bg-brand-green-100',
  },
  [SelfTicketItemType.GENERAL]: {
    icon: HelpCircle,
    label: 'General',
    color: 'text-brand-purple-600',
    bgColor: 'bg-brand-purple-100',
  },
};

// Status configurations
const statusConfigMap: Record<SelfTicketRequestStatus, { bg: string; text: string; icon: typeof Clock; label: string }> = {
  [SelfTicketRequestStatus.PENDING]: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    icon: Clock,
    label: 'Pending',
  },
  [SelfTicketRequestStatus.APPROVED]: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    icon: CheckCircle,
    label: 'Approved',
  },
  [SelfTicketRequestStatus.DENIED]: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    icon: XCircle,
    label: 'Denied',
  },
  [SelfTicketRequestStatus.CLOSED]: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    icon: Lock,
    label: 'Closed',
  },
};

// Format date helper
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Format date with time for timeline (DD MM YYYY, HH:MM AM/PM)
const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${day} ${month} ${year}, ${hour12}:${minutes} ${ampm}`;
};

// Format time helper
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

// Format message date header
const formatMessageDate = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
};

// Full-bleed chat shell. It cancels LearnerLayout's <main> padding
// (p-3 / sm:p-4 / lg:p-5 / xl:p-6) and fills the height left under the sticky
// header (57px below lg — the 40px avatar button + py-2 + border — 64px lg+).
//  - Below lg the old fixed -mx-6/-mt-6 overshot the padding, which caused a
//    sideways scroll and tucked the top of the chat under the header.
//  - On phones 100vh includes the browser's address bar, which pushed the
//    message box off-screen; 100dvh is the visible height (with a 100vh
//    fallback for browsers without dvh).
// lg and up keep the original values.
const CHAT_SHELL_CLASSES = [
  '-m-3 sm:-m-4 lg:m-0 lg:-mx-6 lg:-mt-6 lg:-mb-6',
  'h-[calc(100vh-57px)] supports-[height:100dvh]:h-[calc(100dvh-57px)]',
  'sm:h-[calc(100vh-57px)] sm:supports-[height:100dvh]:h-[calc(100dvh-57px)]',
  'lg:h-[calc(100vh-64px)] lg:supports-[height:100dvh]:h-[calc(100vh-64px)]',
].join(' ');

// Page Skeleton
const PageSkeleton = (): JSX.Element => (
  <div className={`${CHAT_SHELL_CLASSES} flex flex-col bg-gray-50`}>
    {/* Header Skeleton */}
    <div className="bg-white border-b px-4 sm:px-6 py-4">
      <div className="flex items-center gap-4">
        <SkeletonLoader type="custom" className="w-10 h-10 rounded-lg" />
        <div className="space-y-2">
          <SkeletonLoader type="custom" className="h-5 w-48 rounded" />
          <SkeletonLoader type="custom" className="h-4 w-32 rounded" />
        </div>
      </div>
    </div>

    {/* Content Skeleton */}
    <div className="flex-1 flex">
      <div className="flex-1 p-6">
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
              <SkeletonLoader type="custom" className="h-16 w-3/4 sm:w-64 rounded-2xl" />
            </div>
          ))}
        </div>
      </div>
      <div className="hidden lg:block w-80 xl:w-[380px] border-l bg-white p-4">
        <SkeletonLoader type="custom" className="h-full rounded-xl" />
      </div>
    </div>
  </div>
);

// Message Bubble Component
const MessageBubble = ({
  message,
  isOwn,
  showAvatar,
  currentUserId,
  onReact,
  onRemoveReaction,
  onDelete,
  ticketStatus,
  approverIds,
  requesterId,
  senderProfileImage,
}: {
  message: ChatMessage;
  isOwn: boolean;
  showAvatar: boolean;
  currentUserId: string;
  onReact: (messageId: string, emoji: string) => void;
  onRemoveReaction: (messageId: string, emoji: string) => void;
  onDelete: (messageId: string, deleteType: 'for_everyone' | 'for_me') => void;
  ticketStatus: SelfTicketRequestStatus;
  approverIds: string[];
  requesterId: string;
  senderProfileImage?: string;
}): JSX.Element => {
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReactionDetails, setShowReactionDetails] = useState(false);
  const [selectedReactionFilter, setSelectedReactionFilter] = useState<string>('all');
  const reactionRef = useRef<HTMLDivElement>(null);

  // Determine read status for tick display
  // For requester's message: recipients are all approvers (excluding sender)
  // For approver's message: recipient is the requester (excluding sender if same person)
  const isRequesterMessage = message.sender_role === 'requester';
  const rawRecipientIds = isRequesterMessage ? approverIds : [requesterId];
  // Filter out the sender from recipients (handles case where admin is also the requester)
  const recipientIds = rawRecipientIds.filter(id => id !== message.sender_id);

  // Check how many recipients have read
  const recipientsWhoRead = recipientIds.filter(id => message.is_read_by.includes(id));
  const isReadBySome = recipientsWhoRead.length > 0;
  const isReadByAll = recipientIds.length > 0 && recipientsWhoRead.length === recipientIds.length;

  // Check if current user is the actual sender (for delete permissions)
  const isSender = message.sender_id === currentUserId;

  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (reactionRef.current && !reactionRef.current.contains(event.target as Node)) {
        setShowReactionPicker(false);
        setShowActions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleReaction = (emoji: string) => {
    // Check if user already reacted with this emoji
    const existingReaction = message.reactions?.find(r => r.emoji === emoji);
    if (existingReaction?.user_ids.includes(currentUserId)) {
      onRemoveReaction(message.id, emoji);
    } else {
      onReact(message.id, emoji);
    }
    setShowReactionPicker(false);
  };

  const handleDeleteForEveryone = () => {
    onDelete(message.id, 'for_everyone');
    setShowDeleteModal(false);
  };

  const handleDeleteForMe = () => {
    onDelete(message.id, 'for_me');
    setShowDeleteModal(false);
  };

  // Calculate total reactions count
  const totalReactions = message.reactions?.reduce((sum, r) => sum + r.count, 0) || 0;

  // Get filtered users for reaction details modal
  const getFilteredReactionUsers = () => {
    if (!message.reactions) return [];
    const users: { name: string; emoji: string; userId: string }[] = [];
    const reactionsToShow = selectedReactionFilter === 'all'
      ? message.reactions
      : message.reactions.filter(r => r.emoji === selectedReactionFilter);

    reactionsToShow.forEach(reaction => {
      reaction.user_ids.forEach((userId, idx) => {
        users.push({
          name: (reaction.user_names || [])[idx] || 'Unknown User',
          emoji: reaction.emoji,
          userId,
        });
      });
    });
    return users;
  };

  return (
    <>
      <div
        className={`flex items-end gap-2 group ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => { if (!showReactionPicker) setShowActions(false); }}
      >
        {/* Avatar */}
        {showAvatar ? (
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${
              isOwn ? 'bg-primary-100' : 'bg-gray-100'
            }`}
          >
            {senderProfileImage ? (
              <img
                src={senderProfileImage}
                alt={message.sender_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <User className={`w-4 h-4 ${isOwn ? 'text-primary-600' : 'text-gray-600'}`} />
            )}
          </div>
        ) : (
          <div className="w-8 flex-shrink-0" />
        )}

        {/* Message Content */}
        <div className={`max-w-[78%] sm:max-w-[70%] min-w-0 relative ${isOwn ? 'items-end' : 'items-start'}`} ref={reactionRef}>
          {showAvatar && (
            <p className={`text-xs font-medium mb-1 ${isOwn ? 'text-right text-primary-600' : 'text-gray-600'}`}>
              {message.sender_name}
            </p>
          )}

          <div className="relative">
            {/* Message bubble */}
            <div
              className={`px-4 py-2.5 rounded-2xl ${
                message.is_deleted
                  ? 'bg-gray-100 text-gray-400 italic'
                  : isOwn
                    ? 'bg-primary-100 text-gray-900 rounded-br-md'
                    : 'bg-gray-100 text-gray-900 rounded-bl-md'
              }`}
            >
              {/* Long links / words wrap instead of widening the chat */}
              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{message.message}</p>
            </div>

            {/* Reactions display */}
            {message.reactions && message.reactions.length > 0 && !message.is_deleted && (
              <div className={`flex flex-wrap gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                {message.reactions.map((reaction) => (
                  <button
                    key={reaction.emoji}
                    onClick={() => {
                      setSelectedReactionFilter(reaction.emoji);
                      setShowReactionDetails(true);
                    }}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors cursor-pointer ${
                      reaction.user_ids.includes(currentUserId)
                        ? 'bg-primary-100 border-primary-300 text-primary-700'
                        : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span>{reaction.emoji}</span>
                    <span>{reaction.count}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Action buttons (show on hover) - available until ticket is closed */}
            {showActions && !message.is_deleted && ticketStatus !== SelfTicketRequestStatus.CLOSED && (
              /* Mobile: sit just above the bubble, inside its width — beside it
                 (the desktop spot) runs off a narrow screen. Shown on tap. */
              <div className={`absolute z-10 -top-9 sm:top-0 ${isOwn ? 'right-0 sm:right-auto sm:left-0 sm:-translate-x-full sm:pr-2' : 'left-0 sm:left-auto sm:right-0 sm:translate-x-full sm:pl-2'} flex items-center gap-1`}>
                <button
                  onClick={() => setShowReactionPicker(!showReactionPicker)}
                  className="p-1.5 rounded-full bg-white border shadow-sm hover:bg-gray-50 transition-colors"
                  title="Add reaction"
                >
                  <Smile className="w-4 h-4 text-gray-500" />
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="p-1.5 rounded-full bg-white border shadow-sm hover:bg-red-50 transition-colors"
                  title="Delete message"
                >
                  <Trash2 className="w-4 h-4 text-gray-500 hover:text-red-500" />
                </button>
              </div>
            )}

            {/* Quick reaction picker */}
            {showReactionPicker && (
              <div className={`absolute bottom-full mb-2 ${isOwn ? 'right-0' : 'left-0'} bg-white rounded-full shadow-lg border p-1.5 flex gap-1 z-10`}>
                {REACTION_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => handleReaction(emoji)}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-lg"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Timestamp and read status */}
          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
            <p className="text-xs text-gray-400">{formatTime(message.timestamp)}</p>
            {isOwn && !message.is_deleted && (
              <span className={`${isReadByAll ? 'text-primary-500' : 'text-gray-400'}`}>
                {isReadBySome ? (
                  <CheckCheck className="w-4 h-4" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Reaction Details Modal (WhatsApp style) */}
      {showReactionDetails && message.reactions && message.reactions.length > 0 && (
        <div
          className="fixed inset-0 common-modal-overlay bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setShowReactionDetails(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-xs shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">
                {totalReactions} {totalReactions === 1 ? 'reaction' : 'reactions'}
              </h3>
              <button
                onClick={() => setShowReactionDetails(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Emoji filter tabs */}
            <div className="flex items-center gap-1 px-3 py-2 border-b border-gray-100 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              <button
                onClick={() => setSelectedReactionFilter('all')}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                  selectedReactionFilter === 'all'
                    ? 'bg-primary-100 text-primary-700 border border-primary-300'
                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                <Smile className="w-3.5 h-3.5" />
                <span>All {totalReactions}</span>
              </button>
              {message.reactions.map((reaction) => (
                <button
                  key={reaction.emoji}
                  onClick={() => setSelectedReactionFilter(reaction.emoji)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    selectedReactionFilter === reaction.emoji
                      ? 'bg-primary-100 text-primary-700 border border-primary-300'
                      : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <span>{reaction.emoji}</span>
                  <span>{reaction.count}</span>
                </button>
              ))}
            </div>

            {/* User list */}
            <div className="overflow-y-auto max-h-[240px] py-1">
              {getFilteredReactionUsers().map((user, idx) => (
                <div
                  key={`${user.userId}-${user.emoji}-${idx}`}
                  onClick={() => {
                    if (user.userId === currentUserId) {
                      onRemoveReaction(message.id, user.emoji);
                      if (totalReactions <= 1) {
                        setShowReactionDetails(false);
                      }
                    }
                  }}
                  className={`flex items-center gap-3 px-4 py-2 ${
                    user.userId === currentUserId ? 'hover:bg-red-50 cursor-pointer' : ''
                  }`}
                >
                  {/* User avatar */}
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-primary-700">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  {/* Name and action hint */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user.userId === currentUserId ? 'You' : user.name}
                    </p>
                    {user.userId === currentUserId && (
                      <p className="text-[11px] text-gray-400">Click to remove</p>
                    )}
                  </div>
                  {/* Emoji */}
                  <span className="text-lg flex-shrink-0">{user.emoji}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Delete Message Modal (WhatsApp style) */}
      {showDeleteModal && (
        <div className="fixed inset-0 common-modal-overlay bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete message?</h3>
              <p className="text-sm text-gray-600">
                {isSender
                  ? 'Choose how you want to delete this message.'
                  : 'This message will be deleted for you only.'}
              </p>
            </div>
            <div className="border-t">
              {isSender && (
                <button
                  onClick={handleDeleteForEveryone}
                  className="w-full px-6 py-3.5 text-left text-sm border-b text-red-600 hover:bg-red-50"
                >
                  <span className="font-medium">Delete for everyone</span>
                </button>
              )}
              <button
                onClick={handleDeleteForMe}
                className="w-full px-6 py-3.5 text-left text-sm text-gray-700 hover:bg-gray-50 font-medium"
              >
                Delete for me
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="w-full px-6 py-3.5 text-left text-sm text-gray-500 hover:bg-gray-50 border-t"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Ticket Details Sidebar
const TicketDetailsSidebar = ({
  ticket,
  showApproversList,
  setShowApproversList
}: {
  ticket: SelfTicketRequestResponseDto;
  showApproversList: boolean;
  setShowApproversList: (show: boolean) => void;
}): JSX.Element => {
  const typeConfig = typeConfigMap[ticket.item_type] || typeConfigMap[SelfTicketItemType.COURSE];
  const statusConfig = statusConfigMap[ticket.status as SelfTicketRequestStatus] || statusConfigMap[SelfTicketRequestStatus.PENDING];
  const TypeIcon = typeConfig.icon;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      {/* Profile Section */}
      <div className="flex-shrink-0 pt-6 pb-4 px-4">
        {/* Large Profile Image */}
        <div className="flex flex-col items-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 via-indigo-500 to-purple-600 flex items-center justify-center shadow-lg ring-4 ring-white overflow-hidden">
            {ticket.requester_profile_image ? (
              <img
                src={ticket.requester_profile_image}
                alt={ticket.requester_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-white font-bold text-2xl">
                {ticket.requester_name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </span>
            )}
          </div>
          <span className="mt-2 font-mono text-sm font-semibold text-primary-600">{ticket.ticket_number}</span>
          <h3 className="mt-1 font-semibold text-gray-900 text-base">{ticket.requester_name}</h3>
          <p className="text-xs text-gray-500 truncate max-w-full">{ticket.requester_email}</p>

          {/* Status & Type Badges */}
          <div className="flex items-center gap-2 mt-3">
            <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
              <StatusIcon className="w-3 h-3" />
              {statusConfig.label}
            </div>
            <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${typeConfig.bgColor} ${typeConfig.color}`}>
              <TypeIcon className="w-3 h-3" />
              {ticket.item_type === SelfTicketItemType.GENERAL && ticket.requested_suggestion_type
                ? `General - ${typeConfigMap[ticket.requested_suggestion_type]?.label || ticket.requested_suggestion_type}`
                : typeConfig.label}
            </div>
          </div>

          {/* Date */}
          <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-400">
            <Clock className="w-3.5 h-3.5" />
            Requested on {formatDate(ticket.createdAt)}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-4 border-t border-gray-200" />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hide">
        {showApproversList ? (
          /* Approvers List View */
          <div className="space-y-3">
            {/* Header with back button */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Users className="w-4 h-4 text-primary-600" />
                Approvers List
              </h3>
              <button
                onClick={() => setShowApproversList(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>

            {/* Approvers */}
            {ticket.approvers && ticket.approvers.length > 0 ? (
              <div className="space-y-2">
                {ticket.approvers.map((approver, index) => {
                  // Get first and last initials only (e.g., "joe selva P" -> "J P")
                  const nameParts = approver.user_name?.trim().split(/\s+/) || [];
                  const initials = nameParts.length === 0 ? '?'
                    : nameParts.length === 1 ? nameParts[0][0]?.toUpperCase() || '?'
                    : (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();

                  return (
                    <div
                      key={approver.user_id || index}
                      className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-colors"
                    >
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-brand-indigo-600 flex items-center justify-center text-white font-semibold flex-shrink-0 text-sm overflow-hidden">
                        {approver.profile_image ? (
                          <img
                            src={approver.profile_image}
                            alt={approver.user_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          initials
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{approver.user_name}</p>
                        <p className="text-xs text-gray-500 truncate">{approver.email}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No approvers assigned</p>
              </div>
            )}
          </div>
        ) : (
          /* Ticket Details View */
          <>
        {/* Items Card - For GENERAL tickets show Description or Suggested Items */}
        {ticket.item_type === SelfTicketItemType.GENERAL ? (
          <>
            {/* Always show Request Description for GENERAL tickets */}
            <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
              <p className="text-xs font-semibold text-gray-700 mb-2.5 flex items-center gap-1.5">
                <HelpCircle className="w-3.5 h-3.5 text-purple-600" />
                Request Description
              </p>
              <div className="p-3 rounded-lg bg-purple-50 bg-opacity-50">
                <p className="text-xs text-gray-800 leading-relaxed">
                  {ticket.general_description || 'No description provided'}
                </p>
              </div>
            </div>

            {/* Show Language for GENERAL tickets */}
            {ticket.language_code && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                <p className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-indigo-600" />
                  Language
                </p>
                <p className="text-sm text-gray-800 font-medium">{ticket.language_code}</p>
              </div>
            )}

            {/* Show Course Generation Config for COURSE suggestion type */}
            {ticket.requested_suggestion_type === SelfTicketItemType.COURSE && ticket.course_generation_config && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                <p className="text-xs font-semibold text-gray-700 mb-2.5 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5 text-indigo-600" />
                  Course Generation Config
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 rounded-lg bg-indigo-50">
                    <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wide mb-0.5">Delivery Type</p>
                    <p className="text-xs text-gray-800 font-medium capitalize">
                      {ticket.course_generation_config.content_delivery_type.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div className="p-2 rounded-lg bg-indigo-50">
                    <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wide mb-0.5">Design Theme</p>
                    <p className="text-xs text-gray-800 font-medium capitalize">
                      {ticket.course_generation_config.design_theme}
                    </p>
                  </div>
                  <div className="p-2 rounded-lg bg-indigo-50">
                    <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wide mb-0.5">Modules</p>
                    <p className="text-xs text-gray-800 font-medium">
                      {ticket.course_generation_config.module_generation_config.min_modules} – {ticket.course_generation_config.module_generation_config.max_modules}
                    </p>
                  </div>
                  <div className="p-2 rounded-lg bg-indigo-50">
                    <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wide mb-0.5">Content Items / Module</p>
                    <p className="text-xs text-gray-800 font-medium">
                      {ticket.course_generation_config.content_generation_config.min_content_items_per_module} – {ticket.course_generation_config.content_generation_config.max_content_items_per_module}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Show Selected Courses & Learning Paths for CERTIFICATION suggestion type */}
            {ticket.requested_suggestion_type === SelfTicketItemType.CERTIFICATION &&
              ((ticket.certification_courses && ticket.certification_courses.length > 0) ||
               (ticket.certification_learning_paths && ticket.certification_learning_paths.length > 0)) && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                <p className="text-xs font-semibold text-gray-700 mb-2.5 flex items-center gap-1.5">
                  <Award className="w-3.5 h-3.5 text-amber-600" />
                  Requested Items for Certification
                </p>

                {/* Selected Courses */}
                {ticket.certification_courses && ticket.certification_courses.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      Courses ({ticket.certification_courses.length})
                    </p>
                    <div className="space-y-1">
                      {ticket.certification_courses.map((course, index) => (
                        <div
                          key={course.item_id}
                          className="flex items-center gap-2 p-2 rounded-lg bg-indigo-50"
                        >
                          <div className="w-4 h-4 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-[9px] font-bold text-indigo-600">{index + 1}</span>
                          </div>
                          <p className="text-xs text-gray-800 truncate font-medium">{course.item_name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Learning Paths */}
                {ticket.certification_learning_paths && ticket.certification_learning_paths.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      Learning Paths ({ticket.certification_learning_paths.length})
                    </p>
                    <div className="space-y-1">
                      {ticket.certification_learning_paths.map((lp, index) => (
                        <div
                          key={lp.item_id}
                          className="flex items-center gap-2 p-2 rounded-lg bg-green-50"
                        >
                          <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-[9px] font-bold text-green-600">{index + 1}</span>
                          </div>
                          <p className="text-xs text-gray-800 truncate font-medium">{lp.item_name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Show Suggested Items if ticket has been actioned and has items */}
            {ticket.actioned_by?.action === 'SUGGESTED' && ticket.items && ticket.items.length > 0 && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                <p className="text-xs font-semibold text-gray-700 mb-2.5 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <TypeIcon className="w-3.5 h-3.5 text-purple-600" />
                    Suggested Items
                  </span>
                  <span className="bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full text-[10px] font-bold">
                    {ticket.items.length}
                  </span>
                </p>
                <div className="space-y-1.5">
                  {ticket.items.map((item, index) => (
                    <div
                      key={item.item_id}
                      className="flex items-center gap-2.5 p-2.5 rounded-lg bg-purple-50 bg-opacity-50"
                    >
                      <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                        <span className="text-[10px] font-bold text-purple-600">{index + 1}</span>
                      </div>
                      <p className="text-xs text-gray-800 truncate font-medium">{item.item_name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
            <p className="text-xs font-semibold text-gray-700 mb-2.5 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <TypeIcon className={`w-3.5 h-3.5 ${typeConfig.color}`} />
                Requested Items
              </span>
              <span className={`${typeConfig.bgColor} ${typeConfig.color} px-2 py-0.5 rounded-full text-[10px] font-bold`}>
                {(ticket.items || []).length}
              </span>
            </p>
            <div className="space-y-1.5">
              {ticket.items.map((item, index) => (
                <div
                  key={item.item_id}
                  className={`flex items-center gap-2.5 p-2.5 rounded-lg ${typeConfig.bgColor} bg-opacity-50`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <span className={`text-[10px] font-bold ${typeConfig.color}`}>{index + 1}</span>
                  </div>
                  <p className="text-xs text-gray-800 truncate font-medium">{item.item_name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Resolution - Using actioned_by (shown above timeline) */}
        {ticket.actioned_by && (() => {
          const action = ticket.actioned_by.action;
          // Treat SUGGESTED same as APPROVED for display
          const isPositive = action === 'APPROVED' || action === 'SUGGESTED';
          const isDenied = action === 'DENIED';

          const bgClass = isPositive ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
          const iconBgClass = isPositive ? 'bg-green-100' : 'bg-red-100';
          const iconColorClass = isPositive ? 'text-green-600' : 'text-red-600';
          const textColorClass = isPositive ? 'text-green-800' : 'text-red-800';
          const subTextColorClass = isPositive ? 'text-green-600' : 'text-red-600';
          const timeColorClass = isPositive ? 'text-green-500' : 'text-red-500';
          const remarksBgClass = isPositive ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100';
          const actionLabel = isPositive ? 'Approved' : 'Denied';
          const remarksLabel = isDenied ? 'Reason:' : 'Remarks:';

          return (
            <div className={`rounded-xl p-3 border shadow-sm ${bgClass}`}>
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${iconBgClass}`}>
                  {isPositive ? (
                    <CheckCircle className={`w-4 h-4 ${iconColorClass}`} />
                  ) : (
                    <XCircle className={`w-4 h-4 ${iconColorClass}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold ${textColorClass}`}>
                    {actionLabel}
                  </p>
                  <p className={`text-[10px] truncate ${subTextColorClass}`}>
                    by {ticket.actioned_by.user_name} ({ticket.actioned_by.role})
                  </p>
                </div>
              </div>
              {ticket.actioned_by.actioned_at && (
                <p className={`text-[10px] mt-1.5 ${timeColorClass}`}>
                  <Clock className="w-3 h-3 inline mr-1" />
                  {formatDate(ticket.actioned_by.actioned_at)} at {formatTime(ticket.actioned_by.actioned_at)}
                </p>
              )}
              {ticket.actioned_by.remarks && (
                <p className={`text-xs mt-2 rounded-lg p-2 ${remarksBgClass}`}>
                  <span className="font-medium">{remarksLabel}</span> {ticket.actioned_by.remarks}
                </p>
              )}
            </div>
          );
        })()}

        {/* Timeline */}
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-500" />
            Timeline
          </p>
          <div className="relative">
            {/* Vertical line - green for approved/closed, red for denied, default green */}
            <div className={`absolute left-[7px] top-2 bottom-2 w-0.5 ${
              ticket.status === SelfTicketRequestStatus.DENIED ? 'bg-red-200' : 'bg-green-200'
            }`} />

            <div className="space-y-3">
              {/* Created */}
              <div className="flex items-start gap-3 relative">
                <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow-sm z-10 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Ticket Created</p>
                  <p className="text-xs text-gray-500">{formatDateTime(ticket.createdAt)}</p>
                </div>
              </div>

              {/* Read by users - show each reader */}
              {ticket.read_by && ticket.read_by.length > 0 && ticket.read_by.map((reader, index) => (
                <div key={`reader-${index}`} className="flex items-start gap-3 relative">
                  <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow-sm z-10 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{reader.user_name} read the ticket</p>
                    <p className="text-xs text-gray-500">{formatDateTime(reader.read_at)}</p>
                  </div>
                </div>
              ))}

              {/* Approved/Denied - Highlighted as last stage if not closed */}
              {ticket.actioned_by && (() => {
                const action = ticket.actioned_by.action;
                // Treat SUGGESTED same as APPROVED for display
                const isPositive = action === 'APPROVED' || action === 'SUGGESTED';
                const isClosed = ticket.status === SelfTicketRequestStatus.CLOSED;

                const bgColor = isPositive ? 'bg-green-500' : 'bg-red-500';
                const ringColor = isPositive ? 'ring-green-200' : 'ring-red-200';
                const textColor = isClosed ? 'text-gray-900' : isPositive ? 'text-green-700' : 'text-red-700';
                const actionLabel = isPositive ? 'Approved' : 'Denied';

                return (
                  <div className="flex items-start gap-3 relative">
                    <div className={`rounded-full border-2 border-white shadow-sm z-10 mt-0.5 ${bgColor} ${
                      isClosed ? 'w-4 h-4' : `w-5 h-5 ring-2 ${ringColor} -ml-0.5`
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${textColor}`}>
                        {actionLabel}
                      </p>
                      <p className="text-xs text-gray-500">
                        by {ticket.actioned_by.user_name} • {formatDateTime(ticket.actioned_by.actioned_at)}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Closed - Highlighted as last stage */}
              {ticket.status === SelfTicketRequestStatus.CLOSED && ticket.closed_at && (
                <div className="flex items-start gap-3 relative">
                  <div className="w-5 h-5 rounded-full bg-green-500 border-2 border-white shadow-sm z-10 mt-0.5 ring-2 ring-green-200 -ml-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-green-700">Ticket Closed</p>
                    <p className="text-xs text-gray-500">
                      {ticket.closed_by ? `by ${ticket.closed_by.user_name} • ` : ''}{formatDateTime(ticket.closed_at)}
                    </p>
                  </div>
                </div>
              )}

              {/* Pending indicator - Highlighted as last stage */}
              {ticket.status === SelfTicketRequestStatus.PENDING && (
                <div className="flex items-start gap-3 relative">
                  <div className="w-5 h-5 rounded-full bg-green-400 border-2 border-white shadow-sm z-10 mt-0.5 animate-pulse ring-2 ring-green-200 -ml-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-green-700">Awaiting Action</p>
                    <p className="text-xs text-gray-500">Pending approval or denial</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

// Main Component
export default function TicketChat(): JSX.Element {
  // Permission guard - requires WRITE permission to access ticket chat
  usePermissionRouteAccess([LEARNER_TICKETS_PERMISSION.LEARNER_TICKETS_WRITE]);

  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  // Emoji picker size fitted to narrow / short phone screens; read from window
  // in an effect (not during render) and kept current on resize / rotation.
  const [viewportWidth, setViewportWidth] = useState(1024);
  useEffect(() => {
    const update = () => setViewportWidth(window.innerWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const [showClearChatModal, setShowClearChatModal] = useState(false);
  const [showApproversList, setShowApproversList] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fetch ticket details with polling every 5 seconds for real-time messages
  // Also mark messages as read on every fetch so sender sees blue tick
  const {
    data: ticket,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['learner-ticket-detail', ticketId],
    queryFn: async () => {
      // First mark messages as read to update is_read_by on messages
      await ticketService.markMessagesAsReadByRequester(ticketId!).catch(() => {});
      // Then fetch the updated ticket details
      return ticketService.getRequestByIdForLearner(ticketId!);
    },
    enabled: !!ticketId,
    refetchInterval: 5000, // Poll every 5 seconds for new messages
  });

  // Check if it's an access denied error (404 from backend)
  const isAccessDenied = (error as any)?.response?.status === 404 ||
    (error as any)?.response?.data?.error?.error_code === 'request_not_found';

  // Get current user ID from auth context
  const { user } = useAuth();
  const currentUserId = user?.id || '';

  // Send message mutation - uses setQueryData for smooth updates without blinking
  const sendMessageMutation = useMutation({
    mutationFn: (message: string) => ticketService.addMessage(ticketId!, { message }),
    onMutate: async (newMessage: string) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['learner-ticket-detail', ticketId] });

      // Snapshot the previous value
      const previousTicket = queryClient.getQueryData<SelfTicketRequestResponseDto>(['learner-ticket-detail', ticketId]);

      // Optimistically update the cache with new message
      if (previousTicket) {
        const optimisticMessage: TicketMessageDto = {
          _id: `temp-${Date.now()}`,
          sender_id: ticket?.requester_id || 'user-1',
          sender_name: ticket?.requester_name || 'You',
          sender_role: 'REQUESTER',
          message: newMessage,
          sent_at: new Date().toISOString(),
          is_read_by: [ticket?.requester_id || 'user-1'],
          reactions: [],
          is_deleted: false,
        };

        queryClient.setQueryData<SelfTicketRequestResponseDto>(['learner-ticket-detail', ticketId], {
          ...previousTicket,
          messages: [...(previousTicket.messages || []), optimisticMessage],
        });
      }

      // Return context with previous value for rollback
      return { previousTicket };
    },
    onError: (_error, _newMessage, context) => {
      // Rollback to previous value on error
      if (context?.previousTicket) {
        queryClient.setQueryData(['learner-ticket-detail', ticketId], context.previousTicket);
      }
      toast.error('Failed to send message. Please try again.');
    },
    onSettled: () => {
      // Sync with server after mutation (success or error)
      // Use a slight delay to allow polling to pick up the new message naturally
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['learner-ticket-detail', ticketId] });
      }, 1000);
    },
  });

  // Add reaction mutation
  const addReactionMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      ticketService.addReaction(ticketId!, messageId, { emoji }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learner-ticket-detail', ticketId] });
    },
    onError: () => {
      toast.error('Failed to add reaction.');
    },
  });

  // Remove reaction mutation
  const removeReactionMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      ticketService.removeReaction(ticketId!, messageId, { emoji }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learner-ticket-detail', ticketId] });
    },
    onError: () => {
      toast.error('Failed to remove reaction.');
    },
  });

  // Delete message mutation
  const deleteMessageMutation = useMutation({
    mutationFn: ({ messageId, deleteType }: { messageId: string; deleteType: 'for_everyone' | 'for_me' }) =>
      ticketService.deleteMessage(ticketId!, messageId, { delete_type: deleteType }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['learner-ticket-detail', ticketId] });
      toast.success(variables.deleteType === 'for_everyone' ? 'Message deleted for everyone.' : 'Message deleted.');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.error_description || 'Failed to delete message.';
      toast.error(message);
    },
  });

  // Clear chat mutation
  const clearChatMutation = useMutation({
    mutationFn: () => ticketService.clearChat(ticketId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learner-ticket-detail', ticketId] });
      setShowClearChatModal(false);
      toast.success('Chat cleared.');
    },
    onError: () => {
      toast.error('Failed to clear chat.');
    },
  });

  // Form setup
  const { register, handleSubmit, reset, watch, setValue, getValues } = useForm<MessageFormData>({
    defaultValues: { message: '' },
  });

  const messageValue = watch('message');

  // Map API messages to UI format (optimistic updates are handled in the cache)
  const allMessages: ChatMessage[] = (ticket?.messages || []).map((msg) => mapApiMessageToChat(msg, currentUserId));

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages.length]);

  // Close emoji picker and menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Group messages by date
  const groupedMessages = allMessages.reduce((groups, message) => {
    const date = formatMessageDate(message.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {} as Record<string, ChatMessage[]>);

  // Handle emoji selection for input
  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    const currentMessage = getValues('message');
    setValue('message', currentMessage + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  // Handle reaction
  const handleReaction = (messageId: string, emoji: string) => {
    addReactionMutation.mutate({ messageId, emoji });
  };

  // Handle remove reaction
  const handleRemoveReaction = (messageId: string, emoji: string) => {
    removeReactionMutation.mutate({ messageId, emoji });
  };

  // Handle delete message
  const handleDeleteMessage = (messageId: string, deleteType: 'for_everyone' | 'for_me') => {
    deleteMessageMutation.mutate({ messageId, deleteType });
  };

  // Handle clear chat
  const handleClearChat = () => {
    clearChatMutation.mutate();
  };

  // Send message handler
  const onSubmit = (data: MessageFormData): void => {
    if (!data.message.trim() || sendMessageMutation.isPending) return;

    const messageText = data.message.trim();
    reset();

    // Send message via API - optimistic update is handled in onMutate
    sendMessageMutation.mutate(messageText);
  };

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (isError || !ticket) {
    // Show access denied error with styled card
    if (isAccessDenied) {
      return (
        <div className="h-[calc(100vh-200px)] flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            {/* Main Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Top colored bar */}
              <div className="h-1.5 bg-gradient-to-r from-red-500 to-red-600" />

              <div className="p-6">
                {/* Icon */}
                <div className="relative mx-auto w-16 h-16 mb-4">
                  <div className="absolute inset-0 bg-red-100 rounded-full animate-pulse" />
                  <div className="relative w-full h-full bg-gradient-to-br from-red-50 to-red-100 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                    <Shield className="w-7 h-7 text-red-500" />
                  </div>
                </div>

                {/* Title */}
                <h1 className="text-xl font-bold text-gray-900 text-center mb-2">
                  Access Restricted
                </h1>

                {/* Description */}
                <p className="text-gray-500 text-center text-sm mb-5">
                  You don't have permission to view this ticket request. Only the requester can access this page.
                </p>

                {/* Info Box */}
                <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-5">
                  <div className="flex gap-2.5">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-700">
                      <span className="font-medium text-red-800">Looking for your tickets?</span> Go back to view your own ticket requests.
                    </p>
                  </div>
                </div>

                {/* Button */}
                <button
                  onClick={() => navigate('/learn/tickets')}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium text-sm shadow-md shadow-red-500/20"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to My Tickets
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <XCircle className="w-16 h-16 mb-4 text-red-400" />
        <p className="font-semibold text-lg">Failed to load ticket details</p>
        <p className="text-sm mt-1">Please try again later</p>
        <button
          onClick={() => navigate('/learn/tickets')}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Back to Tickets
        </button>
      </div>
    );
  }

  const typeConfig = typeConfigMap[ticket.item_type];
  const TypeIcon = typeConfig.icon;

  return (
    <div className={`${CHAT_SHELL_CLASSES} flex flex-col bg-gray-50 overflow-hidden`}>
      {/* Header */}
      <div className="bg-white border-b px-2 sm:px-6 py-2 sm:py-3 flex-shrink-0">
        <div className="flex items-center justify-between gap-1 sm:gap-2">
          <div className="flex items-center gap-1 sm:gap-4 min-w-0 flex-1">
            <button
              onClick={() => navigate('/learn/tickets')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              aria-label="Back to tickets"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${typeConfig.bgColor}`}>
                <TypeIcon className={`w-5 h-5 ${typeConfig.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                {/* Wraps on mobile; the requester is the viewer, so their name
                    is dropped there (it is in the Details panel). */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                  <span className="font-mono font-semibold text-primary-600">{ticket.ticket_number}</span>
                  <span className="hidden sm:inline text-gray-300">|</span>
                  <span className={`text-xs sm:text-sm font-medium ${typeConfig.color}`}>{typeConfig.label} Ticket</span>
                  <span className="hidden sm:inline text-gray-300">|</span>
                  <span className="hidden sm:inline text-gray-700 font-medium">{ticket.requester_name}</span>
                </div>
                {/* Show request_message below ticket info for all ticket types */}
                {ticket.request_message && (
                  <p className="text-xs text-gray-500 mt-0.5 sm:mt-1 break-words line-clamp-1 sm:line-clamp-none">
                    {ticket.request_message}
                  </p>
                )}
              </div>
            </div>
          </div>
          {/* Mobile/tablet: the details sidebar is hidden below lg, so open it as a panel */}
          <button
            onClick={() => setShowDetailsPanel(true)}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            aria-label="Ticket details"
            title="Ticket details"
          >
            <Info className="w-5 h-5 text-gray-500" />
          </button>
          <div className="relative flex-shrink-0" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MoreVertical className="w-5 h-5 text-gray-500" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border py-1 z-20">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowApproversList(true);
                    // Below lg the sidebar is hidden — show it as a panel.
                    // (The panel itself is lg:hidden, so desktop is unaffected.)
                    setShowDetailsPanel(true);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Users className="w-4 h-4" />
                  Approvers List
                </button>
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowClearChatModal(true);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear Chat
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clear Chat Confirmation Modal */}
      {showClearChatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 common-modal-overlay">
          <div className="bg-white rounded-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Clear Chat</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to clear this chat? Messages will only be hidden for you, not deleted from the server.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearChatModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleClearChat}
                disabled={clearChatMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
              >
                {clearChatMutation.isPending ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chat Section */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 sm:p-6 space-y-6 custom-scrollbar">
            {Object.entries(groupedMessages).map(([date, dateMessages]) => (
              <div key={date}>
                {/* Date Separator */}
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-gray-200 text-gray-600 text-xs font-medium px-3 py-1 rounded-full">
                    {date}
                  </div>
                </div>

                {/* Messages for this date */}
                <div className="space-y-3">
                  {dateMessages.map((message, index) => {
                    // System messages are displayed differently
                    if (message.sender_role === 'system') {
                      return (
                        <div key={message.id} className="flex justify-center my-2">
                          <div className="bg-gray-100 text-gray-600 text-xs px-4 py-1.5 rounded-2xl sm:rounded-full text-center max-w-[90%] break-words">
                            {message.message}
                          </div>
                        </div>
                      );
                    }

                    const isOwn = message.sender_id === currentUserId;
                    const prevMessage = dateMessages[index - 1];
                    const showAvatar = !prevMessage || prevMessage.sender_id !== message.sender_id || prevMessage.sender_role === 'system';

                    // Get sender's profile image
                    const senderProfileImage = message.sender_role === 'requester'
                      ? ticket.requester_profile_image
                      : ticket.approvers?.find(a => a.user_id === message.sender_id)?.profile_image;

                    return (
                      <MessageBubble
                        key={message.id}
                        message={message}
                        isOwn={isOwn}
                        showAvatar={showAvatar}
                        currentUserId={currentUserId}
                        onReact={handleReaction}
                        onRemoveReaction={handleRemoveReaction}
                        onDelete={handleDeleteMessage}
                        ticketStatus={ticket.status}
                        approverIds={ticket.approvers?.map(a => a.user_id) || []}
                        requesterId={ticket.requester_id}
                        senderProfileImage={senderProfileImage}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          {/* pb respects the iPhone home-indicator area */}
          <div className="bg-white border-t px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:p-4 flex-shrink-0">
            {ticket.status !== SelfTicketRequestStatus.CLOSED ? (
              <>
                <form onSubmit={handleSubmit(onSubmit)} className="flex items-end gap-1.5 sm:gap-2">
                  {/* Emoji picker button */}
                  <div className="relative" ref={emojiPickerRef}>
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0 mb-2"
                    >
                      <Smile className="w-5 h-5" />
                    </button>
                    {showEmojiPicker && (
                      <div className="absolute bottom-full left-0 mb-2 z-20">
                        {/* Fit narrow / short phone screens */}
                        <EmojiPicker
                          onEmojiClick={handleEmojiSelect}
                          width={Math.min(320, viewportWidth - 24)}
                          height={viewportWidth < 640 ? 340 : 400}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 relative">
                    <textarea
                      {...register('message')}
                      placeholder="Type your message..."
                      rows={1}
                      /* text-base on mobile: iOS Safari zooms the page into any
                         input whose font is under 16px when it is focused. */
                      className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-base sm:text-sm max-h-32"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmit(onSubmit)();
                        }
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!messageValue?.trim() || sendMessageMutation.isPending}
                    className="p-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 mb-2"
                  >
                    <Send className={`w-5 h-5 ${sendMessageMutation.isPending ? 'animate-pulse' : ''}`} />
                  </button>
                </form>
              </>
            ) : (
              <div className="flex items-center justify-center gap-2 py-3 px-4 bg-gray-100 rounded-xl">
                <Lock className="w-5 h-5 text-gray-600 flex-shrink-0" />
                <p className="text-sm text-gray-600 text-center sm:text-left">This ticket has been <span className="font-medium text-gray-700">closed</span>. Chat is no longer available.</p>
              </div>
            )}
          </div>
        </div>

        {/* Ticket Details Sidebar - Hidden on mobile */}
        <div className="hidden lg:flex lg:flex-col w-80 xl:w-[380px] border-l bg-white flex-shrink-0">
          <TicketDetailsSidebar
            ticket={ticket}
            showApproversList={showApproversList}
            setShowApproversList={setShowApproversList}
          />
        </div>
      </div>

      {/* Mobile/tablet Ticket Details panel — same content as the lg sidebar */}
      {showDetailsPanel && (
        <div className="lg:hidden fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Ticket details">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowDetailsPanel(false)} />
          <div className="relative w-full max-w-sm h-full bg-white shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
              <h3 className="font-semibold text-gray-900">Ticket Details</h3>
              <button
                onClick={() => setShowDetailsPanel(false)}
                className="p-2 -mr-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close ticket details"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <TicketDetailsSidebar
                ticket={ticket}
                showApproversList={showApproversList}
                setShowApproversList={setShowApproversList}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
